// Shared with the boot guard in index.html (vite/boot-guard.js): same key, same budget.
const KEY = 'mira:stale-reload'
const WINDOW_MS = 60_000
const MAX_RELOADS = 2

type Mark = { n: number; t: number }

function storage(): Storage | null {
  try {
    const s = window.sessionStorage
    s.setItem(`${KEY}:probe`, '1')
    s.removeItem(`${KEY}:probe`)
    return s
  } catch {
    return null
  }
}

function read(s: Storage): Mark {
  try {
    const m = JSON.parse(s.getItem(KEY) ?? 'null') as Mark | null
    if (m && Date.now() - m.t < WINDOW_MS) return m
  } catch {
    /* treated as no recent reload */
  }
  return { n: 0, t: 0 }
}

/**
 * Whether a reload may still fix a stale build. Without storage we cannot count reloads, so we never
 * reload on our own: a loop is worse than an error screen with a button.
 */
export function canRecover(): boolean {
  const s = storage()
  return s !== null && read(s).n < MAX_RELOADS
}

/**
 * Reloads into the same URL so the newest build loads. A file that failed is fetched again first, bypassing
 * the browser cache, in case an HTML answer was cached under its name. The second try waits a moment, for a
 * rollout where the old and new server still answer side by side.
 */
export async function recover(failedUrl?: string): Promise<void> {
  const s = storage()
  const mark = s ? read(s) : { n: 0, t: 0 }
  s?.setItem(KEY, JSON.stringify({ n: mark.n + 1, t: Date.now() }))
  if (failedUrl) await fetch(failedUrl, { cache: 'reload' }).catch(() => undefined)
  if (mark.n > 0) await new Promise((r) => setTimeout(r, 1500))
  window.location.reload()
}

/** A plain reload from a button, after refreshing any file that failed. */
export async function reloadNow(failedUrl?: string): Promise<void> {
  if (failedUrl) await fetch(failedUrl, { cache: 'reload' }).catch(() => undefined)
  window.location.reload()
}
